/**
 * views/projects/projectRowCells.jsx - one cell per column id for a Projects
 * row (classes, tooltips, doc links, pills), plus the per-column font size /
 * ellipsis title. Moved verbatim out of ProjectsView.jsx.
 */
import { MarginPill } from '../../components/ui';
import { shortDate } from '../../components/ui/BidTablePrimitives';
import { leftoverOf, computeMargin } from '../../utils/projectMoney';
import ActiveClosedPill, { projectStatus } from '../../components/projects/ActiveClosedPill';
import CellDocLink from '../../components/shared/CellDocLink';
import TasksCell from '../../components/tasks/TasksCell';
import CommentsCell from '../../components/shared/CommentsCell';
import { healthOf, HEALTH } from '../../data/projectHealth';
import { splitNames } from './tableBits';

export function makeRowCells({ view, tl, look, COL_ACCESSORS, taskIndex, commentIndex, docIdFor, money }) {
  // a cell of a column with its own width or font size: the size, and the full
  // value as a tooltip when an ellipsis may cut it
  const cellExtra = (p, col, own) => {
    const size = view.fonts[col] && view.fonts[col].cell;
    const style = size ? { fontSize: size } : null;
    const title = (tl.fixed || !look.wrap) && !own && COL_ACCESSORS[col] ? String(COL_ACCESSORS[col](p) ?? '') || undefined : own;
    return { style, title };
  };

  // One cell per column id, so the person's order and hidden columns decide
  // what a row shows (10/9/26). Dates / money / pills are nowrap;
  // descriptions and customer names wrap so the cell can stay compact.
  const rowCells = (p) => {
    const cogs = (Number(p.quotedLaborCost) || 0) + (Number(p.quotedExpenses) || 0);
    const profit = (Number(p.quotedPrice) || 0) - cogs;
    // a group row's leftover is the sum of its records' (data/projectHierarchy rollupRow)
    const left = p.__group ? p.__group.leftover || null : leftoverOf(p);
    const B = 'border-r border-[#E8E8E8]';
    const empty = <span className="text-text-empty">-</span>;
    return {
      quoteDate: { className: `${B} font-semibold text-text-dark whitespace-nowrap`, children: shortDate(p.quoteDate) || '-' },
      customer: { className: `${B} break-words`, children: p.customer || '-' },
      subCustomer: { className: `${B} text-text-body break-words`, children: p.subCustomer || p.sub_customer || empty },
      description: {
        className: `${B} text-[10.5px] leading-snug break-words`,
        title: p.description || p.name || '',
        children: p.description || p.name || '-',
      },
      tasks: { className: B, children: <TasksCell tasks={taskIndex.get(p.id)} /> },
      comments: { className: B, children: <CommentsCell comments={commentIndex.get(p.id)} /> },
      approvalManager: { className: `${B} text-text-body break-words`, children: p.approvalManager || p.approval_manager || empty },
      engineer: {
        className: `${B} text-text-body break-words`,
        title: p.engineer || '',
        children: splitNames(p.engineer).join(', ') || empty,
      },
      quoteLetter: {
        className: `font-mono text-[10px] text-text-body ${B} whitespace-nowrap`,
        children:
          p.quoteLetter || p.quote_letter ? (
            <CellDocLink attId={docIdFor(p.id, 'quote', p.quoteLetter || p.quote_letter)} text={p.quoteLetter || p.quote_letter} />
          ) : (
            empty
          ),
      },
      rev: { className: `${B} text-center font-mono text-[10px] text-text-body whitespace-nowrap`, children: p.rev || empty },
      quotedPrice: {
        num: true,
        className: `${B} font-semibold whitespace-nowrap`,
        children: p.quotedPrice ? money(p.quotedPrice, p) : '-',
      },
      // PO group
      poDateIssued: { className: `${B} bg-[#FAFBF7] whitespace-nowrap`, children: shortDate(p.poDateIssued) || empty },
      poNumber: {
        className: `font-mono text-[10px] text-text-body ${B} bg-[#FAFBF7] whitespace-nowrap`,
        children: p.poNumber ? <CellDocLink attId={docIdFor(p.id, 'po', p.poNumber)} text={p.poNumber} /> : empty,
      },
      terms: { className: `${B} bg-[#FAFBF7] text-text-body break-words`, title: p.terms || '', children: p.terms || empty },
      poValue: {
        num: true,
        className: `${B} bg-[#FAFBF7] whitespace-nowrap`,
        children: (p.poValue ?? p.po_value) ? money(p.poValue ?? p.po_value, p) : empty,
      },
      globalProjectNumber: {
        className: `font-mono text-[10px] text-text-body ${B} whitespace-nowrap`,
        children: p.globalProjectNumber || p.global_project_number || empty,
      },
      // computed financials
      cogs: { num: true, className: `${B} text-rose-700 whitespace-nowrap`, children: cogs ? money(cogs, p) : '-' },
      profit: { num: true, className: `${B} text-emerald-700 font-semibold whitespace-nowrap`, children: cogs ? money(profit, p) : '-' },
      margin: { num: true, className: B, children: <MarginPill value={computeMargin(p)} /> },
      // invoice group
      latestInvoiceDate: {
        className: `${B} bg-[#FFFBEB] whitespace-nowrap`,
        children: shortDate(p.latestInvoiceDate || p.latest_invoice_date) || empty,
      },
      invoiceNumbers: {
        className: `font-mono text-[10px] text-text-body ${B} bg-[#FFFBEB] leading-[1.35] break-words`,
        title: p.invoiceNumbers || p.invoice_numbers || '',
        children: (() => {
          const raw = p.invoiceNumbers || p.invoice_numbers;
          if (!raw) return empty;
          // each invoice number links to ITS labeled file when one exists
          return String(raw)
            .split(/[,;/]+/)
            .map((x) => x.trim())
            .filter(Boolean)
            .map((n, i) => (
              <span key={`${n}-${i}`}>
                {i > 0 && ', '}
                <CellDocLink attId={docIdFor(p.id, 'invoice', n)} text={n} />
              </span>
            ));
        })(),
      },
      invoicedAlready: {
        num: true,
        className: `${B} bg-[#FFFBEB] whitespace-nowrap`,
        children: p.invoicedAlready != null || p.invoiced_already != null ? money(p.invoicedAlready ?? p.invoiced_already, p) : empty,
      },
      moneyLeftover: {
        num: true,
        className: 'whitespace-nowrap',
        style:
          left > 0
            ? { background: '#FEF3C7', color: '#92400E', fontWeight: 700, fontSize: '12.5px' }
            : left < 0
              ? { background: '#FFEBEE', color: '#B71C1C', fontWeight: 700 }
              : { background: '#FFFBEB' },
        title: left == null ? 'Fully invoiced' : 'PO Value minus Invoiced',
        children: left != null ? money(left, p) : empty,
      },
      status: {
        children: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {healthOf(p) !== 'green' && (
              <span
                title={`${HEALTH[healthOf(p)].label} — right-click the row to change or explain`}
                style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: HEALTH[healthOf(p)].dot }}
              />
            )}
            <ActiveClosedPill value={projectStatus(p)} />
          </div>
        ),
      },
    };
  };
  return { rowCells, cellExtra };
}
